import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Clock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { DT_STATUS, fmtDay, t12, DT_TYPES } from "@/lib/daytime";

export function DateStep({ days, date, setDate, arrival, setArrival, types, needsTime, hours, onBack, onNext }) {
  const [slots, setSlots] = useState(null);
  const arrRef = useRef(null);
  useEffect(() => {
    if (!date) return;
    setSlots(null);
    api.get("/daytime/slots", { params: { date, pass_types: types.join(",") } }).then((r) => { setSlots(r.data); setTimeout(() => arrRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }).catch(() => setSlots({ slots: [] }));
  }, [date, types]);
  const day = days.find((d) => d.date === date);
  const full = day && (day.status === "full");
  const slot = slots?.slots.find((s) => s.time === arrival);
  const ok = date && !full && day?.status !== "closed" && (!needsTime || (slot && slot.ok));
  return (
    <div className="space-y-5" data-testid="step-date">
      <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5">
        <p className="font-display font-black text-navy text-lg">Choose Your Adventure Day</p>
        <p className="text-sm text-slate-500 flex items-center gap-1 mb-3"><Clock className="w-4 h-4" /> Open Daily | {hours}</p>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
          {days.slice(0, 42).map((d) => (
            <button key={d.date} disabled={d.status === "closed"} onClick={() => { setDate(d.date); setArrival(""); }} data-testid={`day-${d.date}`}
              className={`rounded-xl border-2 py-2 text-center transition-transform active:scale-95 ${DT_STATUS[d.status].cls} ${date === d.date ? "ring-4 ring-navy/70" : ""}`}>
              <p className="text-[10px] uppercase">{fmtDay(d.date, { weekday: "short" })}</p>
              <p className="font-black text-sm">{fmtDay(d.date, { month: "short", day: "numeric" })}</p>
              <p className="text-[9px] truncate px-0.5">{d.status === "closed" ? (d.label || "Closed") : d.status === "full" ? "Full" : d.status === "limited" ? "Limited" : "Open"}</p>
            </button>
          ))}
        </div>
      </div>
      {full && <p className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 font-semibold flex gap-2" data-testid="date-full-msg"><AlertTriangle className="w-5 h-5 shrink-0" /> Adventure Rock is fully booked for this date. Please choose another adventure day.</p>}
      {date && !full && needsTime && (
        <div ref={arrRef} className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 scroll-mt-20" data-testid="arrival-section">
          <p className="font-display font-black text-navy text-lg">Select Arrival Time</p>
          {!slots ? <p className="text-sm text-slate-400">Checking times…</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
              {slots.slots.map((s) => (
                <button key={s.time} disabled={!s.ok} onClick={() => setArrival(s.time)} data-testid={`slot-${s.time}`}
                  className={`h-12 rounded-2xl border-2 font-black text-sm transition-colors ${arrival === s.time ? "bg-navy text-white border-navy" : s.ok ? "border-turquoise/50 text-navy hover:bg-turquoise/10" : "border-slate-100 text-slate-300 line-through"}`}>{t12(s.time)}</button>
              ))}
            </div>)}
          {slot && <div className="mt-4 bg-turquoise/10 rounded-2xl p-3 text-sm text-navy" data-testid="session-end">
            <p>Arrival: <b>{t12(slot.time)}</b></p>
            {Object.entries(slot.ends).map(([k, e]) => <p key={k}>{DT_TYPES[k]}: your play session ends at approximately <b>{t12(e)}</b></p>)}
          </div>}
        </div>
      )}
      {date && !needsTime && !full && <p className="bg-turquoise/10 rounded-2xl p-3 text-sm text-navy" data-testid="allday-note">All-Day Rock Pass · {hours} on {fmtDay(date, { weekday: "long", month: "long", day: "numeric" })}</p>}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="h-14 rounded-full px-6" data-testid="step-date-back">Back</Button>
        <Button disabled={!ok} onClick={onNext} className="flex-1 h-14 rounded-full bg-navy text-white font-black" data-testid="step-date-next">NEXT: EXPLORER DETAILS</Button>
      </div>
    </div>
  );
}
